import { ref, computed, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { getPhases, getTasks } from '../api/projects';
import type { Phase, Task } from '../api/projects';
import type { TimesheetRow } from '../api/client';

export interface PhaseCascadeOptions {
  wbs1: () => string;
  timesheetRows: () => TimesheetRow[];
  enabled: () => boolean;
}

/**
 * Shared phase/task cascade logic.
 *
 * The AttributionWizard, InlineFieldPicker and CellDetailModal all use identical
 * cascade behaviour: on-timesheet narrowing → full VP list fallback, auto-pick
 * when one leaf, lazy fetch on first enable. Centralising here prevents drift.
 */
export function usePhaseCascade(opts: PhaseCascadeOptions) {
  const selectedPhase = ref<Phase | null>(null);
  const selectedTask = ref<Task | null>(null);

  const phaseQueryEnabled = computed(() => opts.enabled() && !!opts.wbs1());

  // A project's phase structure does not change while the user is triaging, and
  // the pickers open and close constantly — a one-minute window still cost 19
  // phase and 21 task reads in one session, each a VP round trip (#6241).
  // Refetching is a page reload away if VP does change under them.
  const { data: phasesData, isFetching: phasesLoading, isError: phasesErrored, refetch: refetchPhases } = useQuery({
    queryKey: computed(() => ['phases', opts.wbs1()]),
    queryFn: () => getPhases(opts.wbs1()),
    enabled: phaseQueryEnabled,
    staleTime: Infinity,
    retry: false,
  });

  const taskQueryEnabled = computed(() => opts.enabled() && !!opts.wbs1() && !!selectedPhase.value);

  // retry:false is deliberate — the API retries VP 5xx itself, so a second
  // client-side retry would only multiply the wait before the same answer.
  const { data: tasksData, isFetching: tasksLoading, isError: tasksErrored, refetch: refetchTasks } = useQuery({
    queryKey: computed(() => ['tasks', opts.wbs1(), selectedPhase.value?.wbs2 ?? '']),
    queryFn: () => getTasks(opts.wbs1(), selectedPhase.value!.wbs2),
    enabled: taskQueryEnabled,
    staleTime: Infinity,
    retry: false,
  });

  // On-timesheet narrowing: prefer leaves already on the employee's sheet.
  const onSheetLeaves = computed((): Phase[] => {
    const wbs1 = opts.wbs1();
    if (!wbs1) return [];
    return opts.timesheetRows()
      .filter(r => r.wbs1 === wbs1 && r.wbs2)
      .map(r => ({ wbs2: r.wbs2, name: r.wbs2Name, chargeType: '', status: 'A' }))
      .filter((p, i, arr) => arr.findIndex(x => x.wbs2 === p.wbs2) === i);
  });

  // On-sheet leaves lead the list; they do not replace it. A project with one
  // phase already on the sheet still has all its others, and a picker that
  // cannot reach them is one the user cannot argue with (#6550).
  const phaseCandidates = computed((): Phase[] => {
    const onSheet = onSheetLeaves.value;
    const rest = (phasesData.value?.phases ?? [])
      .filter(p => !onSheet.some(s => s.wbs2 === p.wbs2));
    return [...onSheet, ...rest];
  });

  const onSheetTasks = computed((): Task[] => {
    const wbs1 = opts.wbs1();
    const wbs2 = selectedPhase.value?.wbs2;
    if (!wbs1 || !wbs2) return [];
    return opts.timesheetRows()
      .filter(r => r.wbs1 === wbs1 && r.wbs2 === wbs2 && r.wbs3)
      .map(r => ({ wbs3: r.wbs3, name: r.wbs3Name, chargeType: '', status: 'A' }))
      .filter((t, i, arr) => arr.findIndex(x => x.wbs3 === t.wbs3) === i);
  });

  const taskCandidates = computed((): Task[] => {
    const onSheet = onSheetTasks.value;
    const rest = (tasksData.value?.tasks ?? [])
      .filter(t => !onSheet.some(s => s.wbs3 === t.wbs3));
    return [...onSheet, ...rest];
  });

  // The suggestion. One leaf already on the sheet is what the user almost always
  // wants, so it opens selected and the rest of the list stays a click away; with
  // nothing on the sheet, a project with a single phase is unambiguous either way.
  // Anything else is the user's pick to make.
  const suggestedPhase = computed((): Phase | null => {
    if (onSheetLeaves.value.length === 1) return onSheetLeaves.value[0];
    return phaseCandidates.value.length === 1 ? phaseCandidates.value[0] : null;
  });

  // Fires when the suggestion arrives after opening (e.g. the phases query
  // resolves). reset() applies it directly so the initial open with on-sheet data
  // is already selected without waiting for a watch cycle.
  watch(suggestedPhase, (phase) => {
    if (opts.enabled() && phase && !selectedPhase.value) {
      selectedPhase.value = phase;
    }
  }, { immediate: false });

  function onPhaseChange(phase: Phase | null): void {
    selectedPhase.value = phase;
    selectedTask.value = null;
  }

  function reset(prePhase?: Phase | null, preTask?: Task | null): void {
    selectedPhase.value = prePhase ?? null;
    selectedTask.value = preTask ?? null;
    // If no pre-selection was supplied, open on the suggestion so the picker is
    // ready to confirm without a round-trip to the phases API.
    if (!selectedPhase.value && suggestedPhase.value) {
      selectedPhase.value = suggestedPhase.value;
    }
  }

  // The phase list is unknown, not empty — the same distinction as
  // tasksUnknown one level down (#6238). On-sheet narrowing answers it without
  // VP, so a failed fetch only matters when the sheet carries nothing either.
  const phasesUnknown = computed((): boolean =>
    phasesErrored.value && onSheetLeaves.value.length === 0,
  );

  // The task list is unknown, not empty: VP could not be asked. Only true when
  // the sheet carries no tasks for this phase either, since on-sheet narrowing
  // already answers the question without VP (#6238).
  const tasksUnknown = computed((): boolean =>
    !!selectedPhase.value && tasksErrored.value && onSheetTasks.value.length === 0,
  );

  // True once the tasks query has resolved and returned at least one task for
  // the selected phase. False while loading, when the phase has no tasks, and
  // when the fetch failed — an unanswered question is not an answer of "none".
  const phaseHasTasks = computed((): boolean => {
    if (!selectedPhase.value) return false;
    if (tasksLoading.value || tasksUnknown.value) return false;
    return taskCandidates.value.length > 0;
  });

  // The chargeable leaf is reached when:
  // - a phase is selected, AND
  // - either the phase has no tasks (phase is the leaf), OR a task is also selected.
  //
  // A failed fetch cannot conclude the phase is a leaf. Treating it as one lets
  // a WBS2-only row through to a save VP then rejects (#6238, same shape as
  // Bug 5959 one level up).
  const leafChosen = computed((): boolean => {
    if (!selectedPhase.value) return false;
    if (tasksUnknown.value && !selectedTask.value) return false;
    if (phaseHasTasks.value && !selectedTask.value) return false;
    return true;
  });

  return {
    selectedPhase,
    selectedTask,
    phaseCandidates,
    taskCandidates,
    phasesLoading,
    tasksLoading,
    phasesUnknown,
    tasksUnknown,
    refetchPhases,
    refetchTasks,
    phaseHasTasks,
    leafChosen,
    onPhaseChange,
    reset,
  };
}
